/**
 * Per-project "Save Offline" toggle. When enabled, fetches & caches:
 *   - project detail (already cached by the response interceptor on view)
 *   - photo template categories
 *   - tailgate + jsa forms
 *   - explicit flag in `projects` IndexedDB store so the project shows in
 *     the offline projects list even if not previously visited
 *
 * When disabled, removes the IndexedDB row (live cache entries for the
 * GETs stay until they're naturally overwritten — they're tiny).
 */
import { useEffect, useState } from "react";
import { Cloud, CloudOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  putProjectOffline,
  getProjectOffline,
  removeProjectOffline,
} from "@/lib/offlineDb";

export default function SaveOfflineButton({ projectId, project }) {
  const [savedAt, setSavedAt] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    getProjectOffline(projectId).then((row) => {
      if (alive) setSavedAt(row?.cachedAt || null);
    });
    return () => { alive = false; };
  }, [projectId]);

  const saveOffline = async () => {
    setBusy(true);
    try {
      // Endpoints chosen specifically to avoid 405s:
      //   /projects/{id}            → full project doc (includes JSA inline)
      //   /projects/{id}/tailgates  → array of all daily tailgates (plural!)
      //                              the singular path is POST-only
      const [proj, tg] = await Promise.all([
        api.get(`/projects/${projectId}`).catch(() => ({ data: project })),
        api.get(`/projects/${projectId}/tailgates`).catch(() => ({ data: [] })),
      ]);
      await putProjectOffline(projectId, {
        project: proj.data,
        tailgates: tg.data,
      });
      setSavedAt(Date.now());
      toast.success("Project saved for offline access");
    } catch (e) {
      toast.error("Failed to save offline — try again with a connection.");
    } finally {
      setBusy(false);
    }
  };

  const removeOffline = async () => {
    setBusy(true);
    try {
      await removeProjectOffline(projectId);
      setSavedAt(null);
      toast.success("Removed from offline storage");
    } finally {
      setBusy(false);
    }
  };

  const saved = !!savedAt;
  return (
    <button
      type="button"
      onClick={saved ? removeOffline : saveOffline}
      disabled={busy}
      data-testid="save-offline-btn"
      title={saved ? "Saved for offline. Tap to remove." : "Tap to save this project for offline use."}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs border transition-colors ${
        saved
          ? "bg-emerald-950/40 border-emerald-800 text-emerald-300 hover:bg-emerald-950/60"
          : "bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-600"
      } disabled:opacity-50`}
    >
      {busy ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : saved ? (
        <Cloud className="w-3.5 h-3.5" />
      ) : (
        <CloudOff className="w-3.5 h-3.5" />
      )}
      {saved ? "Saved Offline" : "Save Offline"}
    </button>
  );
}
